import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, ConflictError } from "@/api/client";
import { useTrip, useCloseTrip, useReopenTrip } from "@/hooks/useTrip";
import { useReceipts } from "@/hooks/useReceipts";
import { queryKeys } from "@/hooks/queryKeys";
import { PageShell } from "@/components/PageShell";
import { TripHeader } from "@/components/TripHeader";
import { useShareTrip } from "@/hooks/useShareTrip";
import { Skeleton } from "@/components/Skeleton";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { TextInput } from "@/components/TextInput";
import { CloseReadinessModal } from "@/components/CloseReadinessModal";
import { useToast } from "@/components/toast/ToastProvider";
import { deriveReadiness, needsAttention } from "@/utils/receiptReadiness";
import "./trip-settings.css";

export function TripSettingsScreen() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const tripQ = useTrip(slug);
  const handleShare = useShareTrip(tripQ.data);
  const receiptsQ = useReceipts(slug);
  const close = useCloseTrip(slug);
  const reopen = useReopenTrip(slug);
  const qc = useQueryClient();
  const toast = useToast();

  const [rotateOpen, setRotateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState("");
  const [gateOpen, setGateOpen] = useState(false);

  const receipts = useMemo(
    () => receiptsQ.data?.receipts ?? [],
    [receiptsQ.data],
  );
  const readiness = useMemo(() => deriveReadiness(receipts), [receipts]);
  const blockingReceipts = useMemo(
    () => receipts.filter(needsAttention),
    [receipts],
  );

  if (tripQ.isLoading || !tripQ.data) {
    return (
      <PageShell>
        <Skeleton variant="block" width="100%" height={120} />
      </PageShell>
    );
  }
  const trip = tripQ.data;
  const url = `${window.location.origin}/t/${trip.trip_id}`;

  // HARD GATE (ADR-0029): mirror the settle-up gate so close is blocked from
  // either surface. A doomed request never fires; a racing 409 trip_not_ready
  // surfaces a specific message and routes to the offenders.
  function handleCloseClick(): void {
    if (readiness.counts.total > 0) {
      setGateOpen(true);
      return;
    }
    close.mutate(
      { version: trip.version },
      {
        onError: (err) => {
          if (
            err instanceof ConflictError &&
            err.conflictReason === "trip_not_ready"
          ) {
            toast.push({
              tone: "error",
              title: "Спершу заверши чеки, які ще не враховані.",
            });
            void receiptsQ.refetch();
            void tripQ.refetch();
            navigate(`/t/${trip.trip_id}/receipts?filter=attention`);
          }
        },
      },
    );
  }

  async function handleRotate() {
    setRotateOpen(false);
    try {
      const res = await api.rotateTripSlug(trip.trip_id);
      toast.push({ tone: "success", title: "Лінку оновлено" });
      navigate(`/t/${res.trip_id}/settings`, { replace: true });
    } catch {
      toast.push({ tone: "error", title: "Не вдалось оновити лінку" });
    }
  }

  async function handleDelete() {
    setDeleteOpen(false);
    try {
      await api.deleteTrip(trip.trip_id);
      await qc.invalidateQueries({ queryKey: queryKeys.trip(trip.trip_id) });
      navigate(`/t/${trip.trip_id}/deleted`);
    } catch {
      toast.push({ tone: "error", title: "Не вдалось видалити" });
    }
  }

  return (
    <PageShell header={<TripHeader trip={trip} onShare={handleShare} />}>
      <section className="wp-settings__section">
        <h2 className="wp-settings__heading">Поїздка</h2>
        <p className="wp-settings__caption">Назва: <strong>{trip.name}</strong></p>
        <p className="wp-settings__caption">
          Валюта: <strong>{trip.settlement_ccy}</strong> · змінити не можна.
        </p>
      </section>

      <section className="wp-settings__section">
        <h2 className="wp-settings__heading">Лінка</h2>
        <div className="wp-settings__link">
          <code>{url}</code>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void navigator.clipboard?.writeText(url);
              toast.push({ tone: "success", title: "Скопійовано" });
            }}
          >
            Скопіювати
          </Button>
        </div>
        <p className="wp-settings__caption">
          Будь-хто з цим посиланням бачить і змінює поїздку.
        </p>
        <Button variant="secondary" onClick={() => setRotateOpen(true)}>
          Оновити лінку
        </Button>
      </section>

      <section className="wp-settings__section">
        <h2 className="wp-settings__heading">Стан</h2>
        {trip.status === "open" ? (
          <Button onClick={handleCloseClick} loading={close.isPending}>
            Закрити поїздку
          </Button>
        ) : (
          <Button
            variant="secondary"
            onClick={() => reopen.mutate({ version: trip.version })}
            loading={reopen.isPending}
          >
            Відкрити поїздку
          </Button>
        )}
        <p className="wp-settings__caption">
          Закриття фіксує курси валют і дозволяє позначати перекази як сплачені.
        </p>
      </section>

      <section className="wp-settings__danger">
        <h2 className="wp-settings__heading">Небезпечна зона</h2>
        <Button variant="danger" onClick={() => setDeleteOpen(true)}>
          Видалити поїздку
        </Button>
        <p className="wp-settings__caption">
          Чеки, баланси, позначки — все зникне. 7 днів буде доступне відновлення.
        </p>
      </section>

      <Modal
        open={rotateOpen}
        title="Оновити лінку?"
        description="Стара адреса перестане працювати через 30 днів. Поділись новою лінкою з учасниками."
        confirmLabel="Оновити"
        onConfirm={handleRotate}
        onCancel={() => setRotateOpen(false)}
      />

      <Modal
        open={deleteOpen}
        title="Видалити поїздку?"
        description="Введи назву поїздки для підтвердження."
        variant="destructive"
        confirmLabel="Видалити"
        onConfirm={() => deleteTyped === trip.name && handleDelete()}
        onCancel={() => setDeleteOpen(false)}
      >
        <TextInput
          label={`Введи "${trip.name}"`}
          value={deleteTyped}
          onChange={(e) => setDeleteTyped(e.currentTarget.value)}
        />
      </Modal>

      <CloseReadinessModal
        open={gateOpen}
        tripSlug={trip.trip_id}
        blocking={blockingReceipts}
        transientOnly={readiness.transientOnly}
        onResolve={() => {
          setGateOpen(false);
          navigate(`/t/${trip.trip_id}/receipts?filter=attention`);
        }}
        onCancel={() => setGateOpen(false)}
      />
    </PageShell>
  );
}
